// Pendo product-analytics integration.
//
// Gated behind REACT_APP_PENDO_ENABLED so it can be turned on/off per
// environment. When disabled (or no API key is configured) every exported
// function is a no-op, so callers never need to guard.

const isEnabled = () =>
  process.env.REACT_APP_PENDO_ENABLED === 'true' && !!process.env.REACT_APP_PENDO_PUBLIC_APP_ID;

let initialized = false;

const agentReady = () => typeof window.pendo?.isReady === 'function' && window.pendo.isReady();

// Injects the standard Pendo agent loader. Creates the window.pendo queue
// stub so initialize/identify can be called before the script finishes
// loading. Safe to call multiple times.
export const installPendo = () => {
  if (!isEnabled() || window.pendo) {
    return;
  }

  const publicAppId = process.env.REACT_APP_PENDO_PUBLIC_APP_ID;

  (function (p, e, n, d, o) {
    let v, w, x, y, z;
    o = p[d] = p[d] || {};
    o._q = o._q || [];
    v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    for (w = 0, x = v.length; w < x; ++w)
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(v[w]);
    y = e.createElement(n);
    y.async = !0;
    y.src = 'https://cdn.au.pendo.io/agent/static/' + publicAppId + '/pendo.js';
    z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
  })(window, document, 'script', 'pendo');
};

// Identifies the current user/account with Pendo. Calls pendo.initialize on
// the first identify and pendo.identify on subsequent calls, so it is safe to
// invoke from every auth flow (login, refresh, session restore).
export const identifyPendo = ({ userId, username, fullName, tenantId, tenantName, roles } = {}) => {
  if (!isEnabled() || !window.pendo || !userId) {
    return;
  }

  const envPrefix = process.env.REACT_APP_PENDO_ENV_PREFIX || '';

  const payload = {
    visitor: {
      id: `${envPrefix}${userId}`,
      username: username,
      fullName: fullName,
      roles: roles,
    },
    account: {
      id: `${envPrefix}${tenantId}`,
      name: tenantName,
    },
  };

  if (initialized || agentReady()) {
    window.pendo.identify(payload);
    initialized = true;
  } else {
    window.pendo.initialize(payload);
    initialized = true;
  }
};

// Stops the current Pendo session on logout so the next user is not conflated
// with the previous one.
export const clearPendo = () => {
  if (!window.pendo) {
    return;
  }

  if (typeof window.pendo.clearSession === 'function') {
    window.pendo.clearSession();
  } else if (typeof window.pendo.stopSendingEvents === 'function') {
    window.pendo.stopSendingEvents();
  }
};
